/**
 * Browser-side cleaning of imported Mailchimp archive HTML.
 *
 * The server (functions/src/importMailchimp.ts) fetches the archive and returns
 * its raw HTML; this runs in the browser with a real DOM to do the cleaning
 * that preserves the rich design while removing what shouldn't be re-sent:
 *
 *   - click-tracking links rewritten to their real destination
 *   - open-tracking pixels removed
 *   - the Mailchimp "view in browser" bar and referral badge removed
 *
 * Unlike sanitizeEmailHtml (built for simple Gmail pastes), this keeps tables,
 * inline styles, and images so the campaign looks exactly as designed.
 */

import { unwrapMailchimpTrackingUrl } from '../../functions/src/shared/mailchimp';

export interface MailchimpCleanReport {
  trackingLinksUnwrapped: number;
  trackingPixelsRemoved: number;
  chromeBlocksRemoved: number;
  /** Leftover Mailchimp merge tags (*|FNAME|*) that need manual attention. */
  mergeTagsFound: number;
}

export interface MailchimpCleanResult {
  html: string;
  report: MailchimpCleanReport;
}

/** Selectors for Mailchimp's own scaffolding that isn't part of the email. */
const CHROME_SELECTORS = [
  '#awesomewrap', // referral badge ("powered by Mailchimp")
  '.mcnPreviewText', // hidden preview text
  '#templatePreheader .mcnTextContent a', // handled below; kept for reference
];

/** True when an <img> is an invisible open-tracking beacon. */
function isTrackingPixel(img: HTMLImageElement): boolean {
  const width = img.getAttribute('width');
  const height = img.getAttribute('height');
  if ((width === '1' && height === '1') || (width === '0' && height === '0')) return true;

  const src = img.getAttribute('src') ?? '';
  return /list-manage\.com\/track\/open|\/track\/open|\.mailchimp\.com\/track\/open/i.test(src);
}

/**
 * Remove the "This email was sent to … view it in your browser" bar and the
 * Mailchimp referral badge. These are Mailchimp archive chrome, not content.
 */
function removeChrome(doc: Document): number {
  let removed = 0;

  for (const selector of CHROME_SELECTORS) {
    doc.querySelectorAll(selector).forEach((el) => {
      el.remove();
      removed++;
    });
  }

  // The "view this email in your browser" link lives in the preheader; drop the
  // whole preheader block when its only link points back at the archive.
  doc.querySelectorAll('a').forEach((a) => {
    const text = (a.textContent ?? '').toLowerCase();
    const href = a.getAttribute('href') ?? '';
    if (
      /view (this )?(email|message) in (your )?browser/.test(text) ||
      /campaign-archive\.com|list-manage\.com\/track\/click.*archive/i.test(href)
    ) {
      const block = a.closest('td, tr, div');
      if (block) {
        block.remove();
        removed++;
      }
    }
  });

  return removed;
}

/**
 * Clean imported Mailchimp archive HTML, preserving the design. Returns the
 * cleaned body HTML and a report of what changed.
 */
export function cleanMailchimpHtml(rawHtml: string): MailchimpCleanResult {
  const report: MailchimpCleanReport = {
    trackingLinksUnwrapped: 0,
    trackingPixelsRemoved: 0,
    chromeBlocksRemoved: 0,
    mergeTagsFound: 0,
  };

  const doc = new DOMParser().parseFromString(rawHtml ?? '', 'text/html');

  // Rewrite click-tracking links to their real destinations.
  doc.querySelectorAll('a[href]').forEach((anchor) => {
    const real = unwrapMailchimpTrackingUrl(anchor.getAttribute('href') ?? '');
    if (real) {
      anchor.setAttribute('href', real);
      report.trackingLinksUnwrapped++;
    }
  });

  // Remove open-tracking pixels.
  doc.querySelectorAll('img').forEach((img) => {
    if (isTrackingPixel(img as HTMLImageElement)) {
      img.remove();
      report.trackingPixelsRemoved++;
    }
  });

  report.chromeBlocksRemoved = removeChrome(doc);

  // Count leftover Mailchimp merge tags so the UI can warn the user.
  report.mergeTagsFound = (doc.body.innerHTML.match(/\*\|[A-Z0-9_]+(?::[^|]*)?\|\*/gi) ?? []).length;

  return { html: doc.body.innerHTML.trim(), report };
}

/** Human-readable summary of an import, for a UI notice. */
export function summarizeMailchimpClean(report: MailchimpCleanReport): string {
  const parts: string[] = [];
  if (report.trackingLinksUnwrapped) parts.push(`${report.trackingLinksUnwrapped} tracking link(s) restored`);
  if (report.trackingPixelsRemoved) parts.push(`${report.trackingPixelsRemoved} tracking pixel(s) removed`);
  if (report.chromeBlocksRemoved) parts.push('Mailchimp chrome removed');

  let summary = parts.length ? `Imported — ${parts.join(', ')}.` : 'Imported.';
  if (report.mergeTagsFound) {
    summary += ` ${report.mergeTagsFound} Mailchimp merge tag(s) (like *|FNAME|*) remain — replace them with {{ }} fields or plain text.`;
  }
  return summary;
}
